import React, { forwardRef, type JSX } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, IconButton } from '@mui/material';
import Checkbox from 'components/UI/Checkbox';
import { Background, Border, Main, Text } from 'styles/themeColors';
import styled, { css } from 'styled-components';
import Icon from 'view/Icon';
import Pagination from './Pagination';
import { extendedPaginationSelectOptions } from 'constants/paginationOptions';
import EditCell from './EditCell';
import { renderToast, toast } from './ToastNotification';
import KeyboardArrowDownOutlined from '@mui/icons-material/KeyboardArrowDownOutlined';
import { FontWeight } from 'lib/styles';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';

const ButtonIcon = styled(Icon).attrs({ size: 24 }) <any>`
  path {
      ${({ fillColor }) => fillColor && css`
        fill: ${fillColor};
      `}
  }
`;

interface ColumnGroup {
    category: string;
    columns: string[];
}

interface Props {
    headers: ColumnGroup[];
    data: any[];
    orderBy: string;
    order: 'asc' | 'desc';
    selectedRows: number[];
    onSort: (column: string) => void;
    onSelectAll: (checked: boolean) => void;
    onSelect: (id: number) => void;
    onDelete?: () => void;
    totalCount: number;
    rowsPerPage: number;
    currentPage: number;
    setPage: (page: number) => void;
    setRowsPerPage: (rowsPerPage: number) => void;
    getFieldName: (field: string) => string;
    onEdit?: (id: number, value: any, column: string) => void;
    onAdd?: () => void;
    customHeaderComponent?: () => JSX.Element;
    heightLightErrors?: boolean;
    disabledDeleteToastMessage?: string;
    seletableTotalCount?: number;
    maxHeight?: string;
    readOnly?: boolean;
    disabled?: boolean;
}

const MultiHeaderTable: React.FC<Props> = ({
    headers,
    data,
    orderBy,
    order,
    selectedRows,
    onSort,
    onSelectAll,
    onSelect,
    onDelete,
    totalCount,
    rowsPerPage,
    currentPage,
    setPage,
    setRowsPerPage,
    getFieldName,
    onEdit,
    customHeaderComponent,
    heightLightErrors = false,
    disabledDeleteToastMessage = '',
    seletableTotalCount = totalCount,
    maxHeight = '500px',
    readOnly = false,
}) => {
    const allColumns = headers.flatMap(group => group.columns);

    const SortArrow = forwardRef<SVGSVGElement, { className: string }>(
        ({ className, ...props }, ref) => (
            <KeyboardArrowDownOutlined {...props} ref={ref} className={className} />
        )
    );

    return (
        <>
            <TableContainer style={{ maxHeight: maxHeight, marginBottom: 16, border: `1px solid ${Border.light}`, borderRadius: 12, borderTopWidth: 0, borderBottomWidth: 0 }}>
                <Table>
                    <TableHead style={{ zIndex: 1, position: "relative" }}>
                        <TableRow>
                            <TableCell width="50px" align="left" colSpan={1} style={{ backgroundColor: Background.light, fontSize: 16, padding: 0 }}>
                                <IconButton size="large">
                                    <ReadOnlyWrapper readOnly={readOnly}>
                                        <ButtonIcon
                                            name='DeleteVariants'
                                            onClick={readOnly ? undefined : onDelete}
                                            fillColor={selectedRows.length > 0 ? Main.error2 : Text.secondary}
                                        />
                                    </ReadOnlyWrapper>
                                </IconButton>
                            </TableCell>
                            {
                                customHeaderComponent ?
                                    <TableCell
                                        colSpan={headers?.[0].columns.length}
                                        style={{
                                            backgroundColor: Background.light,
                                            fontSize: 16,
                                            padding: 0,
                                            borderTopRightRadius: 12
                                        }}
                                    >
                                        {customHeaderComponent()}
                                    </TableCell> :

                                    headers.map((group, index) => (
                                        <TableCell
                                            key={group.category}
                                            align="center"
                                            colSpan={group.columns.length}
                                            style={{
                                                backgroundColor: Background.light,
                                                fontSize: 16,
                                                padding: 0,
                                                borderTopRightRadius: index === headers.length - 1 ? 12 : 0
                                            }}

                                        >
                                            {group.category}
                                        </TableCell>
                                    ))
                            }
                        </TableRow>

                        {/* Column Headers */}
                        <TableRow>
                            <TableCell align="center" style={{ backgroundColor: Background.white, fontWeight: FontWeight.bold, fontSize: 16, height: 40, padding: '5px 0px 0px 0px' }}>
                                <ReadOnlyWrapper readOnly={readOnly}>
                                    <Checkbox
                                        id="table-editable-cell-checkbox-id"
                                        checkboxSize="xs"
                                        appearance="none"
                                        checked={selectedRows.length === seletableTotalCount && seletableTotalCount > 0}
                                        onChange={(e) => onSelectAll(e)}
                                        theme={selectedRows.length === seletableTotalCount && seletableTotalCount > 0 ? 'brandLight' : 'lightGray'}
                                        bgColor={selectedRows.length === seletableTotalCount && seletableTotalCount > 0 ? Main.brandLight : Background.light}
                                        disabled={readOnly}
                                    />
                                </ReadOnlyWrapper>
                            </TableCell>
                            {allColumns.map((column) => (
                                <TableCell
                                    key={column}
                                    align="left"
                                    sortDirection={orderBy === column ? order : false}
                                    style={{ backgroundColor: Background.white, fontSize: 16, height: 40, padding: 0, paddingLeft: 7 }}

                                >
                                    <TableSortLabel
                                        active={orderBy === column}
                                        direction={orderBy === column ? order : 'asc'}
                                        onClick={() => onSort(column)}
                                        IconComponent={SortArrow}
                                        className='heading'
                                    >
                                        {getFieldName(column)}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {data.map((row, rowIndex) => (
                            <TableRow key={row.id} style={{ cursor: 'pointer' }}>
                                <TableCell align="center" style={{ backgroundColor: Background.white, fontWeight: FontWeight.bold, fontSize: 14, minHeight: 35, padding: '5px 0px 0px 0px' }}>
                                    <div
                                        onClick={(e) => {
                                            if (row.disableDelete && disabledDeleteToastMessage) {
                                                renderToast(toast.TYPE.INFO, disabledDeleteToastMessage, { autoClose: 3000 });
                                            }
                                        }}
                                    >
                                        <ReadOnlyWrapper readOnly={readOnly}>
                                            <Checkbox
                                                id={`checkbox-${row.id}`}
                                                checkboxSize="xs"
                                                appearance="none"
                                                checked={selectedRows.includes(row.id)}
                                                onChange={() => row.disableDelete ? null : onSelect(row.id)}
                                                theme={selectedRows.includes(row.id) ? 'brandLight' : 'lightGray'}
                                                bgColor={row.disableDelete ? Main.lightGray : selectedRows.includes(row.id) ? Main.brandLight : Background.light}
                                                disabled={readOnly}
                                            />
                                        </ReadOnlyWrapper>
                                    </div>
                                </TableCell>
                                {allColumns.map((column, index) => (
                                    <EditCell
                                        error={heightLightErrors && data[rowIndex]?.errors?.includes(column)}
                                        onChange={(e, column) => onEdit?.(row.id, e, column)}
                                        value={row[column]}
                                        columnIndex={index}
                                        type={typeof row[column]}
                                        disabled={!onEdit || readOnly}
                                        column={column}
                                    />
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer >
            <Pagination
                baseRoute=''
                selectorOptions={extendedPaginationSelectOptions}
                totalPages={Math.ceil(totalCount / rowsPerPage)}
                itemsPerPage={rowsPerPage}
                currentPage={currentPage + 1}
                setCurrentPage={(page: number) => setPage(page - 1)}
                setItemsPerPage={setRowsPerPage}
                shouldRedirect={false}
            />
        </>
    );
};

export default React.memo(MultiHeaderTable);
